export const REFINER_POLL_INTERVAL_MS = 5000;
// Give up after ~2 minutes of uninterrupted poll failures (proxy/refiner down,
// session expired) — the job may still finish in the background.
export const REFINER_MAX_CONSECUTIVE_FAILURES = 24;
// Extra back-to-back attempts within one tick when the fetch promise rejects.
export const REFINER_FETCH_RETRIES = 2;
// Hard cap on how long we watch a job that never reaches a terminal outcome.
export const REFINER_WATCH_TIMEOUT_MS = 3 * 60 * 60 * 1000;
// The api client surfaces HTTP errors as fail Results; the promise only
// rejects on a network-level failure (offline blip, connection reset), which
// is worth a couple of immediate retries before the tick is written off.
// Returns null when every attempt rejected — never throws.
const fetchWithRetries = async (fetchJob) => {
    for (let attempt = 0; attempt <= REFINER_FETCH_RETRIES; attempt += 1) {
        try {
            // eslint-disable-next-line no-await-in-loop
            return await fetchJob();
        }
        catch (_a) {
            // rejected — retry, or fall through to null after the last attempt
        }
    }
    return null;
};
// Polls the refiner job until a terminal outcome and reports it via callbacks.
// Returns an idempotent stop function; after stop() (external or internal)
// no callback ever fires again, even from a fetch already in flight.
export const watchRefinerJob = (fetchJob, callbacks) => {
    let stopped = false;
    let inFlight = false;
    let consecutiveFailures = 0;
    let elapsedMs = 0;
    let intervalId = 0;
    const stop = () => {
        stopped = true;
        window.clearInterval(intervalId);
    };
    intervalId = window.setInterval(async () => {
        if (inFlight) {
            return; // previous poll still running — skip this tick
        }
        elapsedMs += REFINER_POLL_INTERVAL_MS;
        inFlight = true;
        const result = await fetchWithRetries(fetchJob);
        inFlight = false;
        if (stopped) {
            return;
        }
        // null (all attempts rejected) counts like any other failed poll.
        if (result === null || result.isFail()) {
            if ((result === null || result === void 0 ? void 0 : result.err()) === 'project-not-found') {
                stop();
                callbacks.onFailure(null);
                return;
            }
            consecutiveFailures += 1;
            if (consecutiveFailures >= REFINER_MAX_CONSECUTIVE_FAILURES) {
                stop();
                callbacks.onGiveUp();
            }
            return;
        }
        consecutiveFailures = 0;
        const job = result.get();
        if (job.beey_pushed_at != null) {
            stop();
            callbacks.onSuccess(job);
        }
        else if (job.status === 'failed' || job.beey_push_error != null) {
            stop();
            callbacks.onFailure(job);
        }
        else if (elapsedMs >= REFINER_WATCH_TIMEOUT_MS) {
            stop();
            callbacks.onGiveUp();
        }
        else {
            callbacks.onProgress(job);
        }
    }, REFINER_POLL_INTERVAL_MS);
    return stop;
};
